import { useEffect, useState } from "react";

import { api, ApiError } from "../lib/api";
import { getToken } from "../lib/auth";
import { Pager } from "../components/Pager";
import { ConfirmDialog, PromptDialog, AlertDialog } from "../components/ConfirmDialog";
import { useAutoPageSize } from "../lib/useAutoPageSize";
import { usePersistedPage } from "../lib/usePersistedPage";
import { useScrollLock, useModalKeys } from "../lib/useScrollLock";

/**
 * 网站备份 / 迁移（仅超管）。服务端备份历史库：
 *  - 左：手动创建备份（选模块）/ 导入备份 / 自动备份设置
 *  - 右：备份历史列表，每条 下载 / 恢复 / 重命名 / 置顶 / 删除
 * 恢复时先读该备份实际含哪些模块，再让用户勾选恢复哪些。
 */

type ModuleDef = { key: string; label: string; disabled?: boolean };
// 顺序对齐后台菜单。disabled=灰度不可选（基金模块尚未开发完）。
const MODULES: ModuleDef[] = [
  { key: "content", label: "页面文案" },
  { key: "projects", label: "投资项目" },
  { key: "users", label: "用户管理" },
  { key: "studio", label: "智库" },
  { key: "news", label: "动态新闻" },
  { key: "bp", label: "BP 提交" },
  { key: "jobs", label: "招聘信息" },
  { key: "contact", label: "联系咨询" },
  { key: "funds", label: "基金数据", disabled: true },
  { key: "admins", label: "系统数据" },
];
// 默认勾选：除灰度模块外全选
const DEFAULT_KEYS = MODULES.filter((m) => !m.disabled).map((m) => m.key);

type Backup = { id: number; name: string; size: number; modules: string[]; source: string; pinned: boolean; includes_files: boolean; created_at: string };
type Settings = { enabled: boolean; frequency: string; time: string; max_keep: number };

function fmtTime(s: string) { return new Date(s).toLocaleString("zh-CN", { hour12: false }); }
// 文件大小：B / KB / MB / GB（按 1024 进位，保留 1 位小数）
function fmtSize(n: number) {
  if (!n || n < 1024) return `${n || 0} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

// 操作图标按钮：hover 显示 title。color 用于删除红/置顶高亮。
function IconBtn({ title, onClick, color, children }: { title: string; onClick: () => void; color?: string; children: React.ReactNode }) {
  return (
    <button title={title} onClick={onClick}
      style={{ background: "none", border: "none", padding: 4, cursor: "pointer", color: color || "var(--gold)", display: "inline-flex", alignItems: "center" }}>
      {children}
    </button>
  );
}
const I = { width: 18, height: 18, fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, viewBox: "0 0 24 24" };
const IconDownload = () => <svg {...I}><path d="M12 3v12M7 11l5 5 5-5M4 21h16" /></svg>;
const IconRestore = () => <svg {...I}><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>;
const IconRename = () => <svg {...I}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>;
const IconTrash = () => <svg {...I}><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>;
const IconPin = ({ filled }: { filled: boolean }) => <svg {...I} fill={filled ? "currentColor" : "none"}><path d="M12 17v5M9 3h6l-1 7 3 3H7l3-3-1-7Z" /></svg>;

// 把后端导入校验返回的机器码 detail 翻译成中文。未识别的码原样附上，便于排查。
function importErrorText(e: unknown): string {
  if (!(e instanceof ApiError)) return `导入失败：${String(e)}`;
  const detail = (e.body && typeof e.body === "object" && "detail" in e.body) ? String((e.body as { detail: unknown }).detail) : "";
  const [code, arg] = detail.split(":");
  switch (code) {
    case "not_a_zip": return "文件不是有效的 zip 压缩包，无法解析。";
    case "missing_metadata": return "压缩包内缺少 metadata.json，不是本系统导出的备份文件。";
    case "bad_metadata": return "备份描述信息（metadata.json）损坏或格式错误。";
    case "version_mismatch": return "备份文件版本与当前系统不兼容，无法导入。";
    case "no_known_modules": return "备份未包含任何可识别的数据模块。";
    case "missing_table": return `备份不完整：缺少「${arg}」的数据文件。`;
    case "corrupt_table": return `备份损坏：「${arg}」的数据文件无法解析。`;
    default: return detail ? `导入失败：${detail}` : `导入失败（${e.status}）。`;
  }
}

export function BackupPage() {
  const [list, setList] = useState<Backup[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [restoreFor, setRestoreFor] = useState<Backup | null>(null);
  const [renameFor, setRenameFor] = useState<Backup | null>(null);
  const [delFor, setDelFor] = useState<Backup | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [fullConfirm, setFullConfirm] = useState(false);

  const [settings, setSettings] = useState<Settings | null>(null);

  const [page, setPage] = usePersistedPage("kexi_backup_page");
  const { containerRef, pageSize } = useAutoPageSize({ fallbackRow: 88, min: 3 });

  const loadList = () => api.get<Backup[]>("/api/admin/backup/list").then(setList).catch((e) => setMsg(String(e)));
  const loadSettings = () => api.get<Settings>("/api/admin/backup/settings").then(setSettings).catch(() => {});
  useEffect(() => { loadList(); loadSettings(); }, []);

  const download = async (b: Backup) => {
    const res = await fetch(`/api/admin/backup/${b.id}/download`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) { setMsg("下载失败"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${b.name}.zip`; a.click();
    URL.revokeObjectURL(url);
  };

  const doRename = async (name: string) => {
    if (renameFor && name !== renameFor.name) { await api.patch(`/api/admin/backup/${renameFor.id}`, { name }); loadList(); }
    setRenameFor(null);
  };
  const togglePin = async (b: Backup) => { await api.patch(`/api/admin/backup/${b.id}`, { pinned: !b.pinned }); loadList(); };
  const doDelete = async () => {
    if (delFor) { await api.delete(`/api/admin/backup/${delFor.id}`); loadList(); }
    setDelFor(null);
  };

  const onImport = async (f: File) => {
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.postForm("/api/admin/backup/import", fd);
      setMsg("已导入"); loadList();
    } catch (e) {
      setErrMsg(importErrorText(e));
    } finally { setBusy(false); }
  };

  // 整站备份（仅数据）：全模块、不含对象存储文件，文件靠 rclone 单独迁移。
  // 成功后直接刷新历史列表，不再额外提示。
  const fullBackup = async () => {
    setFullConfirm(false);
    setBusy(true); setMsg(null);
    try {
      await api.post("/api/admin/backup/create", { files: false });
      loadList();
    } catch (e) { setMsg(e instanceof ApiError ? `创建失败 ${e.status}` : String(e)); }
    finally { setBusy(false); }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setBusy(true); setMsg(null);
    try { const s = await api.put<Settings>("/api/admin/backup/settings", settings); setSettings(s); setMsg("设置已保存"); }
    catch (e) { setMsg(String(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">网站备份 / 迁移</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
            备份存于对象存储，可下载到本地、在另一台服务器导入恢复 = 整站迁移。文件地址按目标存储自动适配。
          </div>
        </div>
      </div>

      {msg && <div className="muted" style={{ marginBottom: 12 }}>{msg}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 24, alignItems: "start" }}>
        {/* 左栏 */}
        <div style={{ display: "grid", gap: 16 }}>
          <div className="form">
            <div className="ce-label" style={{ marginBottom: 8 }}>手动备份</div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>按模块选择，含对象存储文件。</div>
            <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy} onClick={() => setCreateOpen(true)}>创建备份</button>
          </div>

          <div className="form">
            <div className="ce-label" style={{ marginBottom: 8 }}>导入备份</div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>上传备份文件进备份历史。</div>
            <label className="btn" style={{ width: "100%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", height: 38, boxSizing: "border-box" }}>
              选择文件
              <input type="file" accept=".zip" style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onImport(f); e.target.value = ""; }} />
            </label>
          </div>

          <div className="form">
            <div className="ce-label" style={{ marginBottom: 8 }}>整站备份（仅数据）</div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
              一键备份全部数据表（不含文件）。文件请用 rclone 单独迁移，点击迁移数据（仅文件）按钮查看。
            </div>
            <button className="btn" style={{ width: "100%", marginBottom: 10 }} disabled={busy} onClick={() => setFullConfirm(true)}>整站备份（仅数据）</button>
            <button className="btn" style={{ width: "100%" }} onClick={() => setMigrateOpen(true)}>迁移数据（仅文件）</button>
          </div>

          {settings && (
            <div className="form">
              <div className="ce-label" style={{ marginBottom: 12 }}>自动备份设置</div>
              {/* 自动备份尚未实现：整个设置区暂时灰度不可用 */}
              <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, fontSize: 13, opacity: 0.4, cursor: "not-allowed" }}>
                启用自动备份
                <input type="checkbox" checked={settings.enabled} disabled
                  onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} />
              </label>
              {/* 频率/时间/数量整组灰度不可选（功能未实现） */}
              <fieldset disabled style={{ border: "none", padding: 0, margin: 0, opacity: 0.4 }}>
                <label className="ce-label">备份频率</label>
                <select value={settings.frequency} onChange={(e) => setSettings({ ...settings, frequency: e.target.value })} style={{ width: "100%", marginBottom: 10 }}>
                  <option value="daily">每天</option>
                  <option value="weekly">每周</option>
                  <option value="monthly">每月</option>
                </select>
                <label className="ce-label">备份时间</label>
                <input type="time" value={settings.time} onChange={(e) => setSettings({ ...settings, time: e.target.value })} style={{ width: "100%", marginBottom: 10 }} />
                <label className="ce-label">最大备份数量</label>
                <input type="number" min={1} max={100} value={settings.max_keep} onChange={(e) => setSettings({ ...settings, max_keep: Number(e.target.value) })} style={{ width: "100%", marginBottom: 12 }} />
              </fieldset>
              <div className="muted" style={{ fontSize: 11, marginBottom: 10 }}>※ 自动备份功能开发中，暂未开放。</div>
              <button className="btn btn-primary" style={{ width: "100%" }} disabled onClick={saveSettings}>保存</button>
            </div>
          )}
        </div>

        {/* 右栏：历史列表（按页面高度自动分页）*/}
        <div className="form">
          <div className="ce-label" style={{ marginBottom: 12 }}>备份历史</div>
          {list === null && <div className="muted" style={{ fontSize: 13 }}>加载中…</div>}
          {list && list.length === 0 && <div className="muted" style={{ fontSize: 13 }}>暂无备份。</div>}
          {list && list.length > 0 && (
            <div ref={containerRef}>
              <div style={{ display: "grid", gap: 10 }}>
                {list.slice((page - 1) * pageSize, page * pageSize).map((b) => (
                  <div key={b.id} style={{ position: "relative", border: "1px solid var(--border)", padding: "14px 16px" }}>
                    {/* 置顶图标：右上角，右边缘与下方删除按钮对齐（padding 4 + 卡片内边距 16） */}
                    <button title={b.pinned ? "取消置顶" : "置顶"} onClick={() => togglePin(b)}
                      style={{ position: "absolute", top: 10, right: 16, background: "none", border: "none", padding: 4, cursor: "pointer", color: b.pinned ? "var(--gold)" : "var(--mute-2)" }}>
                      <IconPin filled={b.pinned} />
                    </button>
                    <div style={{ fontSize: 14, paddingRight: 28 }}>
                      {b.name}
                      <span className="muted" style={{ fontSize: 11, marginLeft: 8 }}>{b.source === "auto" ? "自动" : b.source === "import" ? "导入" : "手动"}</span>
                      {!b.includes_files && <span style={{ fontSize: 11, marginLeft: 8, color: "var(--gold)" }}>整站 · 仅数据</span>}
                    </div>
                    {/* 第二行：时间 + 大小在左，操作图标靠右 */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                      <span className="muted" style={{ fontSize: 11 }}>{fmtTime(b.created_at)} · {fmtSize(b.size)}</span>
                      <div style={{ display: "flex", gap: 8 }}>
                        <IconBtn title="下载" onClick={() => download(b)}><IconDownload /></IconBtn>
                        <IconBtn title="恢复" onClick={() => setRestoreFor(b)}><IconRestore /></IconBtn>
                        <IconBtn title="重命名" onClick={() => setRenameFor(b)} color="var(--mute)"><IconRename /></IconBtn>
                        <IconBtn title="删除" onClick={() => setDelFor(b)} color="#b04a3a"><IconTrash /></IconBtn>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* 分页条靠右，与卡片内容右侧对齐 */}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Pager page={page} pageSize={pageSize} total={list.length} onChange={setPage} />
              </div>
            </div>
          )}
        </div>
      </div>

      {createOpen && <CreateModal onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); loadList(); }} setMsg={setMsg} />}
      {restoreFor && <RestoreModal backup={restoreFor} onClose={() => setRestoreFor(null)} onDone={() => { setRestoreFor(null); loadList(); }} onSuccess={setOkMsg} setMsg={setMsg} />}
      {migrateOpen && <MigrateModal onClose={() => setMigrateOpen(false)} />}

      <PromptDialog
        open={renameFor !== null}
        title="重命名"
        label="请输入备份名称"
        initialValue={renameFor?.name || ""}
        onConfirm={doRename}
        onCancel={() => setRenameFor(null)}
      />
      <ConfirmDialog
        open={delFor !== null}
        title="删除备份"
        message={delFor ? <>确认删除「{delFor.name}」？此操作不可撤销。</> : null}
        confirmLabel="删除"
        danger
        onConfirm={doDelete}
        onCancel={() => setDelFor(null)}
      />
      <ConfirmDialog
        open={fullConfirm}
        title="整站备份（仅数据）"
        message={<>将导出全部数据表为一个备份（不含对象存储文件）。文件请用 rclone 单独迁移。确认创建？</>}
        confirmLabel="创建备份"
        onConfirm={fullBackup}
        onCancel={() => setFullConfirm(false)}
      />
      <AlertDialog
        open={errMsg !== null}
        title="导入失败"
        message={errMsg}
        danger
        onClose={() => setErrMsg(null)}
      />
      <AlertDialog
        open={okMsg !== null}
        title="恢复完成"
        message={okMsg}
        onClose={() => setOkMsg(null)}
      />
    </div>
  );
}

/** 创建备份：选模块 */
function CreateModal({ onClose, onDone, setMsg }: { onClose: () => void; onDone: () => void; setMsg: (s: string) => void }) {
  const [sel, setSel] = useState<Set<string>>(new Set(DEFAULT_KEYS));
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (k: string) => { const n = new Set(sel); n.has(k) ? n.delete(k) : n.add(k); setSel(n); };
  const submit = async () => {
    if (sel.size === 0) { setMsg("请至少勾选一个模块"); return; }
    setBusy(true);
    try { await api.post("/api/admin/backup/create", { modules: [...sel], name: name.trim() || undefined }); onDone(); }
    catch (e) { setMsg(e instanceof ApiError ? `创建失败 ${e.status}` : String(e)); setBusy(false); }
  };
  useModalKeys(true, { onClose, onSubmit: submit });
  return (
    <Backdrop onClose={onClose}>
      <div className="ce-label" style={{ marginBottom: 12 }}>创建备份 · 选择模块</div>
      <input placeholder="备份名称（选填）" value={name} onChange={(e) => setName(e.target.value)} style={{ width: "100%", marginBottom: 12 }} />
      <ModuleChecklist sel={sel} onToggle={toggle} />
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
        <button className="btn" onClick={onClose}>取消</button>
        <button className="btn btn-primary" disabled={busy} onClick={submit}>{busy ? "创建中…" : "创建备份"}</button>
      </div>
    </Backdrop>
  );
}

/** 恢复（整库覆盖，二次确认）：
 *  - 整站·仅数据包：不选模块，直接整库覆盖全部数据表。
 *  - 按模块包：读该备份含哪些模块，勾选恢复哪些。 */
function RestoreModal({ backup, onClose, onDone, onSuccess, setMsg }: { backup: Backup; onClose: () => void; onDone: () => void; onSuccess: (s: string) => void; setMsg: (s: string) => void }) {
  const isFull = backup.includes_files === false; // 整站·仅数据：全库覆盖，不分模块
  const [avail, setAvail] = useState<string[] | null>(isFull ? backup.modules : null);
  const [sel, setSel] = useState<Set<string>>(new Set(isFull ? backup.modules : []));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (isFull) return; // 整站包无需读取模块
    api.get<{ modules: string[] }>(`/api/admin/backup/${backup.id}/modules`)
      .then((d) => { setAvail(d.modules); setSel(new Set(d.modules)); })
      .catch(() => { setAvail(backup.modules); setSel(new Set(backup.modules)); });
  }, [backup.id, isFull]);
  const toggle = (k: string) => { const n = new Set(sel); n.has(k) ? n.delete(k) : n.add(k); setSel(n); };
  const submit = async () => {
    // 整站包恢复全部模块；按模块包用勾选集
    const modules = isFull ? backup.modules : [...sel];
    if (modules.length === 0) { setMsg("请至少勾选一个模块"); return; }
    setBusy(true);
    try {
      const r = await api.post<{ files_restored: number; restored: Record<string, number> }>(`/api/admin/backup/${backup.id}/restore`, { modules });
      const rows = Object.values(r.restored).reduce((s, n) => s + n, 0);
      const parts = [`共恢复 ${rows} 条数据`];
      if (r.files_restored > 0) parts.push(`${r.files_restored} 个文件`);
      onSuccess(`已从「${backup.name}」${parts.join("、")}。`);
      onDone();
    } catch (e) { setMsg(e instanceof ApiError ? `恢复失败 ${e.status}: ${JSON.stringify(e.body)}` : String(e)); setBusy(false); }
  };
  useModalKeys(true, { onClose, onSubmit: () => { if (!busy && avail) submit(); } });
  return (
    <Backdrop onClose={onClose}>
      <div className="ce-label" style={{ marginBottom: 6, color: "#b04a3a" }}>
        恢复{isFull ? "整站数据" : "备份"}「{backup.name}」（整库覆盖）
      </div>
      {isFull ? (
        <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
          该备份为整站·仅数据，将清空并覆盖全部数据表（不含文件）。文件请用 rclone 单独迁移。此操作不可撤销。
        </div>
      ) : (
        <>
          <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>该备份含以下模块，勾选要恢复的部分。会先清空对应数据再导入。</div>
          {avail === null ? <div className="muted">读取备份内容…</div> : <ModuleChecklist sel={sel} onToggle={toggle} only={avail} />}
        </>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
        <button className="btn" onClick={onClose}>取消</button>
        <button className="btn" style={{ borderColor: "#b04a3a", color: "#b04a3a" }} disabled={busy || !avail} onClick={submit}>{busy ? "恢复中…" : "覆盖恢复"}</button>
      </div>
    </Backdrop>
  );
}

function ModuleChecklist({ sel, onToggle, only }: { sel: Set<string>; onToggle: (k: string) => void; only?: string[] }) {
  const items = only ? MODULES.filter((m) => only.includes(m.key)) : MODULES;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      {items.map((m) => (
        <label key={m.key} style={{
          display: "flex", alignItems: "center", gap: 10, fontSize: 13,
          cursor: m.disabled ? "not-allowed" : "pointer", opacity: m.disabled ? 0.4 : 1,
        }}>
          <input type="checkbox" checked={sel.has(m.key)} disabled={m.disabled} onChange={() => onToggle(m.key)} />
          {m.label}{m.disabled && <span className="muted" style={{ fontSize: 11 }}>（暂未开放）</span>}
        </label>
      ))}
    </div>
  );
}

/** 迁移数据（仅文件）：纯说明弹窗，展示用 rclone 镜像对象存储 bucket 的命令与步骤 */
function MigrateModal({ onClose }: { onClose: () => void }) {
  useModalKeys(true, { onClose, onSubmit: onClose });
  const code: React.CSSProperties = {
    display: "block", whiteSpace: "pre-wrap", wordBreak: "break-all",
    background: "var(--code-bg, #f5f5f5)", padding: "8px 10px", borderRadius: 4,
    fontSize: 12, fontFamily: "monospace", marginBottom: 12,
  };
  return (
    <Backdrop onClose={onClose}>
      <div className="ce-label" style={{ marginBottom: 8 }}>迁移数据（仅文件）</div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 14, lineHeight: 1.6 }}>
        备份只含数据库（不含文件）。对象存储里的文件（图片/附件等）用 <b>rclone</b> 直接镜像整个 bucket，
        MinIO / 腾讯COS / Cloudflare R2 均 S3 兼容。因文件地址是相对路径，迁移后无需任何改动。
      </div>
      <div style={{ fontSize: 13, marginBottom: 6 }}>① 配置源 + 目的两个 remote（均选 s3，填 endpoint/key/secret/region）：</div>
      <code style={code}>rclone config</code>
      <div style={{ fontSize: 13, marginBottom: 6 }}>② 先演练（--dry-run 只列出将上传/更新/删除的文件，不动数据）：</div>
      <code style={code}>rclone sync 源remote:源bucket 目的remote:目的bucket --dry-run --progress --fast-list</code>
      <div style={{ fontSize: 13, marginBottom: 6 }}>③ 确认无误后正式镜像整桶（支持断点续传/并发/校验）：</div>
      <code style={code}>rclone sync 源remote:源bucket 目的remote:目的bucket --progress --checksum</code>
      <div style={{ fontSize: 13, marginBottom: 6 }}>④ 校验两边对象数一致（可选）：</div>
      <code style={code}>rclone size 源remote:源bucket{"\n"}rclone size 目的remote:目的bucket</code>
      <div className="muted" style={{ fontSize: 11, marginBottom: 14 }}>
        整站迁移流程：先 rclone 搬文件 → 后台「整站备份（仅数据）」导出 zip → 新服务器「导入」该 zip 并恢复。详见部署文档。
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button className="btn btn-primary" onClick={onClose}>知道了</button>
      </div>
    </Backdrop>
  );
}

function Backdrop({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  useScrollLock(true);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div className="form" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: "90%", background: "var(--bg, #fff)", padding: 24 }}>{children}</div>
    </div>
  );
}
